import {
	Query,
	QueryConstraint,
	limit,
	limitToLast,
	startAt,
	startAfter,
	endAt,
	endBefore,
	query,
	QueryDocumentSnapshot,
} from '@angular/fire/firestore'
import { FirestoreFilter } from './firestore-filter'
import { FirestoreSort } from './firestore-sort'

export class FirestoreQueryConstraintsBuilder {
	// private constructor() {}

	static withSort(value: FirestoreSort): FirestoreQueryConstraintsBuilder {
		return new FirestoreQueryConstraintsBuilder().withSort(value)
	}

	static withFilters(value: Array<FirestoreFilter>): FirestoreQueryConstraintsBuilder {
		return new FirestoreQueryConstraintsBuilder().withFilters(value)
	}

	withSort(value: FirestoreSort | undefined): FirestoreQueryConstraintsBuilder {
		this.sort = value
		return this
	}

	withFilters(value: Array<FirestoreFilter> | undefined): FirestoreQueryConstraintsBuilder {
		this.filters = value ?? []
		return this
	}

	withLimit(value: number): FirestoreQueryConstraintsBuilder {
		this.limitToLast = undefined
		this.limit = value
		return this
	}

	withLimitToLast(value: number): FirestoreQueryConstraintsBuilder {
		this.limit = undefined
		this.limitToLast = value
		return this
	}

	withStartAt(
		value: QueryDocumentSnapshot<unknown>,
		withoutEndCursor: boolean = true,
	): FirestoreQueryConstraintsBuilder {
		if (withoutEndCursor) {
			this.withoutEndCursor()
		}
		this.startAfter = undefined
		this.startAt = value
		return this
	}

	withEndAt(
		value: QueryDocumentSnapshot<unknown>,
		withoutStartCursor: boolean = true,
	): FirestoreQueryConstraintsBuilder {
		if (withoutStartCursor) {
			this.withoutStartCursor()
		}
		this.endBefore = undefined
		this.endAt = value
		return this
	}

	withStartAfter(
		value: QueryDocumentSnapshot<unknown>,
		withoutEndCursor: boolean = true,
	): FirestoreQueryConstraintsBuilder {
		if (withoutEndCursor) {
			this.withoutEndCursor()
		}
		this.startAt = undefined
		this.startAfter = value
		return this
	}

	withEndBefore(
		value: QueryDocumentSnapshot<unknown>,
		withoutStartCursor: boolean = true,
	): FirestoreQueryConstraintsBuilder {
		if (withoutStartCursor) {
			this.withoutStartCursor()
		}
		this.endAt = undefined
		this.endBefore = value
		return this
	}

	withoutStartCursor(): FirestoreQueryConstraintsBuilder {
		this.startAt = undefined
		this.startAfter = undefined
		return this
	}

	withoutEndCursor(): FirestoreQueryConstraintsBuilder {
		this.endAt = undefined
		this.endBefore = undefined
		return this
	}

	build(): QueryConstraint[] {
		const constraints: QueryConstraint[] = []

		// Add sorting constraints
		if (this.sort) {
			constraints.push(this.sort.toConstraint())
		}

		// Add filtering constraints
		this.filters.forEach((filter) => {
			constraints.push(filter.toConstraint())
		})

		// Add cursor constraints
		if (this.startAt) {
			constraints.push(startAt(this.startAt))
		} else if (this.startAfter) {
			constraints.push(startAfter(this.startAfter))
		}

		if (this.endAt) {
			constraints.push(endAt(this.endAt))
		} else if (this.endBefore) {
			constraints.push(endBefore(this.endBefore))
		}

		// Add limit constraints
		if (this.limit) {
			constraints.push(limit(this.limit))
		} else if (this.limitToLast) {
			constraints.push(limitToLast(this.limitToLast))
		}

		return constraints
	}

	private sort: FirestoreSort | undefined
	private filters: Array<FirestoreFilter> = []
	private limit: number | undefined
	private limitToLast: number | undefined
	private startAt: QueryDocumentSnapshot<unknown> | undefined
	private startAfter: QueryDocumentSnapshot<unknown> | undefined
	private endAt: QueryDocumentSnapshot<unknown> | undefined
	private endBefore: QueryDocumentSnapshot<unknown> | undefined
}
